import { Component, Inject, Input, OnInit, PLATFORM_ID, Renderer2, ViewChild } from '@angular/core';
import { ResellerMapService } from './reseller-map.service';
import {
  FindAllResellerResponseDto,
  FindAllUserForContactForResponseDto,
  FindAllVeaBoltForContactResponseDto,
  MergedUsersAndVeaShops,
  NtfcMapsMarker,
} from './reseller-map.interfaces';
import { FormBuilder, FormControl } from '@angular/forms';
import { debounceTime, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { GoogleMap } from '@angular/google-maps';
import { EnvironmentVariablesService } from '../../../core/services/environment-variable-service/environment-variables.service';
import { ExposedEnvironmentVariable } from '../../../../environments/environment-variables';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { MapTypeEnum } from '../../../global/global.enums';
import { strings } from '../../functions/global-string.functions';

@Component({
  selector: 'ose-reseller-map',
  templateUrl: './reseller-map.component.html',
  styleUrls: ['./reseller-map.component.scss'],
})
export class ResellerMapComponent implements OnInit {
  @Input() mapType: MapTypeEnum;
  mapTypeEnum = MapTypeEnum;

  paginator = {
    page: 1,
    pageSize: 3,
  };
  users: FindAllUserForContactForResponseDto[] = [];
  veaShop: FindAllVeaBoltForContactResponseDto[] = [];

  searchForUser = true;
  searchForVeaShop = true;

  searchResellerFormControl: FormControl;
  loadingSubject$ = new Subject<boolean>();

  filteredUsers: FindAllUserForContactForResponseDto[] = [];
  filteredVeaShop: FindAllVeaBoltForContactResponseDto[] = [];

  //google maps config
  zoom = 6.7;
  defaultLat = 47.1994366;
  defaultLng = 19.7398875;
  center: google.maps.LatLngLiteral = { lat: this.defaultLat, lng: this.defaultLng };
  options: google.maps.MapOptions = {
    mapTypeId: 'roadmap',
    maxZoom: 18,
    minZoom: 6.7,
  };

  googleMapsAPILoaded = false;

  markers: NtfcMapsMarker[] = [];

  netfoneOffice = {
    lat: '47.465646',
    lng: '19.039786',
    name: 'Netfone központi iroda',
    icon: './assets/images/netfone-dot.webp',
    image: './assets/images/netfone_logo_normal_nobg.webp',
    address: '1119, Budapest',
    address2: 'Mohai út 38.',
  };

  netfoneOfficeMarker: NtfcMapsMarker;

  currentLocation: NtfcMapsMarker = {
    position: {
      lat: 0,
      lng: 0,
    },
    title: 'Jelenlegi tartózkodási helyem',
  };

  @ViewChild('map', { static: false }) map: GoogleMap;

  constructor(
    private resellerMapService: ResellerMapService,
    private fb: FormBuilder,
    private environmentVariablesService: EnvironmentVariablesService,
    private renderer2: Renderer2,
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document: Document
  ) {
    if (!this.mapType) {
      this.mapType = MapTypeEnum.ALL;
    }
  }

  ngOnInit(): void {
    if (this.document.getElementById('gmapsJS') === null) {
      const script: HTMLScriptElement = this.renderer2.createElement('script');
      script.type = `text/javascript`;
      script.src = `https://maps.googleapis.com/maps/api/js?libraries=places&key=${this.environmentVariablesService.getVar(
        ExposedEnvironmentVariable.MAPS_KEY
      )}`;
      script.id = 'gmapsJS';
      script.onload = () => {
        this.googleMapsAPILoaded = true;
      };
      this.renderer2.appendChild(this.document.head, script);
    } else {
      this.googleMapsAPILoaded = true;
    }

    this.resellerMapService.findAllReseller().subscribe((response) => {
      switch (this.mapType) {
        case MapTypeEnum.ALL: {
          this.searchForUser = true;
          this.searchForVeaShop = true;
          this.generateUsersFromResponse(response);
          this.generateShopsFromResponse(response);
          break;
        }
        case MapTypeEnum.FRANCHISE_PARTNER_ONLY: {
          this.searchForUser = true;
          this.searchForVeaShop = false;
          this.generateUsersFromResponse(response);
          this.veaShop = [];
          this.filteredVeaShop = [];
          break;
        }
        case MapTypeEnum.RESELLER_ONLY: {
          this.searchForUser = false;
          this.searchForVeaShop = true;
          this.generateShopsFromResponse(response);
          this.users = [];
          this.filteredUsers = [];
          break;
        }
      }

      this.createMarkers();
    });

    this.searchResellerFormControl = this.fb.control(null);

    this.searchResellerFormControl.valueChanges
      .pipe(
        tap(() => {
          this.loadingSubject$.next(true);
        }),
        debounceTime(500)
      )
      .subscribe(() => {
        this.loadingSubject$.next(false);
        Promise.all([this.filterUsers(), this.filterShops()]).then(() => {
          this.createMarkers();
        });
      });
  }

  changeUserSearchCb(event: MouseEvent): void {
    event.preventDefault();
    this.searchForUser = !this.searchForUser;
    Promise.all([this.filterUsers(), this.filterShops()]).then(() => {
      this.createMarkers();
    });
  }

  changeVeaShopSearchCb(event: MouseEvent): void {
    event.preventDefault();
    this.searchForVeaShop = !this.searchForVeaShop;
    Promise.all([this.filterUsers(), this.filterShops()]).then(() => {
      this.createMarkers();
    });
  }

  filterUsers(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.filteredUsers = [];
      if (this.searchForUser) {
        if (this.searchResellerFormControl.value && this.searchResellerFormControl.value.trim()) {
          this.users.forEach((i) => {
            const searchData = i.nev + i.telepules;

            if (searchData?.toLocaleLowerCase().includes(this.searchResellerFormControl.value.toLocaleLowerCase())) {
              this.filteredUsers.push(i);
            }
          }, resolve());
        } else {
          this.filteredUsers = [...this.users];
          resolve();
        }
      } else {
        resolve();
      }
    });
  }

  filterShops(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.filteredVeaShop = [];
      if (this.searchForVeaShop) {
        if (this.searchResellerFormControl.value && this.searchResellerFormControl.value.trim()) {
          this.veaShop.forEach((i) => {
            const searchData = [i.boltNeve, i.iranyitoszam, i.telepules, i.cim, i.cimKiegeszites].join(' ');
            if (
              searchData?.toLocaleLowerCase().includes(this.searchResellerFormControl.value.toLocaleLowerCase().trim())
            ) {
              this.filteredVeaShop.push(i);
            }
          }, resolve());
        } else {
          this.filteredVeaShop = [...this.veaShop];
          resolve();
        }
      } else {
        resolve();
      }
    });
  }

  zoomInOnMarker(marker: NtfcMapsMarker): void {
    if (marker) {
      this.zoom = 17;
      this.map.panTo({
        lat: marker.position.lat,
        lng: marker.position.lng,
      });
    }
  }

  navigateToCurrentPosition(): void {
    if (this.currentLocation) {
      this.zoom = 14;
      this.map.panTo({
        lat: this.currentLocation.position.lat,
        lng: this.currentLocation.position.lng,
      });
      this.resellerMapService
        .getCity(this.currentLocation.position.lat, this.currentLocation.position.lng)
        .then((response) => {
          if (response) {
            this.searchResellerFormControl.patchValue(response, { emitEvent: true });
          }
        });
    }
  }

  fewData(): boolean {
    return this.filteredUsers.length + this.filteredVeaShop.length < 3;
  }

  mapMarkerClick(marker: NtfcMapsMarker): void {
    this.zoomInOnMarker(marker);
    this.searchResellerFormControl.patchValue(marker.address);
  }

  mergeUsersAndVeaShops(
    users: FindAllUserForContactForResponseDto[],
    shops: FindAllVeaBoltForContactResponseDto[]
  ): MergedUsersAndVeaShops[] {
    const mergedData: MergedUsersAndVeaShops[] = [];
    users.forEach((user) => {
      mergedData.push({ data: user });
    });
    shops.forEach((shop) => {
      mergedData.push({ data: shop });
    });
    return mergedData;
  }

  isVeaShop(data: FindAllUserForContactForResponseDto | FindAllVeaBoltForContactResponseDto): boolean {
    return !!(data as FindAllVeaBoltForContactResponseDto).crmAzonosito;
  }

  private createMarkers(): void {
    this.markers = [];
    this.filteredVeaShop.forEach((shop) => {
      const marker = this.getMarkerForLocation(
        +shop.latitude,
        +shop.longitude,
        // this.initService.initResponse.netfoneUrl + '/assets/images/locations/location-green.webp',
        'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
        shop.boltNeve,
        [shop.iranyitoszam, shop.telepules, shop.cim, shop.cimKiegeszites].join(' ')
      );
      shop.marker = marker;
      this.pushMarkerToArray(marker);
    });
    this.filteredUsers.forEach((user) => {
      const marker = this.getMarkerForLocation(
        +user.latitude,
        +user.longitude,
        // this.initService.initResponse.netfoneUrl + '/assets/images/locations/location-blue.webp',
        'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
        user.nev,
        user.telepules
      );
      user.marker = marker;
      this.pushMarkerToArray(marker);
    });

    const netfoneMarker = this.getMarkerForLocation(
      +this.netfoneOffice.lat,
      +this.netfoneOffice.lng,
      `${this.environmentVariablesService.getVar(ExposedEnvironmentVariable.NETFONE_URL)}/${this.netfoneOffice.icon}`,
      this.netfoneOffice.name
    );
    this.netfoneOfficeMarker = netfoneMarker;
    this.pushMarkerToArray(netfoneMarker);
    this.getCurrentPosition();
  }

  private getCurrentPosition(): void {
    if (isPlatformBrowser(this.platformId)) {
      if (navigator && 'geolocation' in navigator && isPlatformBrowser(this.platformId)) {
        navigator.geolocation.getCurrentPosition((position) => {
          this.currentLocation.position.lat = position.coords.latitude;
          this.currentLocation.position.lng = position.coords.longitude;
          const currentPositionMarker = this.getMarkerForLocation(
            this.currentLocation.position.lat,
            this.currentLocation.position.lng,
            'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
            this.currentLocation.title
          );
          this.pushMarkerToArray(currentPositionMarker);
        });
      }
    }
  }

  private getMarkerForLocation(lat: number, lng: number, icon: string, name: string, address?: string): NtfcMapsMarker {
    return {
      position: {
        lat: lat,
        lng: lng,
      },
      title: name,
      options: {
        icon: icon,
      },
      address: address,
    };
  }

  private pushMarkerToArray(marker: NtfcMapsMarker): void {
    this.markers.push(marker);
  }

  private generateUsersFromResponse(response: FindAllResellerResponseDto): void {
    this.users = response.userForContactResponseDTOList;
    strings.sort(this.users, 'telepules');
    this.filteredUsers = [...this.users];
  }

  private generateShopsFromResponse(response: FindAllResellerResponseDto): void {
    this.veaShop = response.veaBoltForContactResponseDTOList;
    strings.sort(this.veaShop, 'boltNeve');
    this.filteredVeaShop = [...this.veaShop];
  }
}
